import type {
  BoardPayloadAuthor,
  BoardPayloadAssignee,
  BoardPayloadIssueItem,
  BoardPayloadIssueType,
  BoardPayloadItem,
  BoardPayloadLabel,
  BoardPayloadLinkedPullRequest,
  BoardPayloadMilestone,
  BoardPayloadParentIssue,
  BoardPayloadRepository,
  BoardPayloadSelectOption,
  BoardPayloadSubIssuesProgress,
  CustomFieldIdentifier,
} from './board-payload'
import {isBoardPayloadSelectOption} from './board-payload'
import {FieldDataType, type FieldDefinition, FieldIdentifier} from '../../saved-view-options/fields'

/** One visible single-select field with an option chosen on this item. */
export interface CardSingleSelect {
  identifier: string
  /** The field's name, which a search filters it by. */
  fieldName: string
  option: BoardPayloadSelectOption
}

/**
 * One visible multi-select field with at least one option chosen on this item. Its options keep
 * the order the server sends, which is the field editor's order.
 */
export interface CardMultiSelect {
  identifier: string
  fieldName: string
  options: readonly BoardPayloadSelectOption[]
}

/** The item's visible custom issue fields that have a value, by type, each in `fieldDefinitions` order. */
export interface CardIssueFields {
  singleSelect: readonly CardSingleSelect[]
  multiSelect: readonly CardMultiSelect[]
}

/**
 * The attributable field values a card should render, already gated by which fields are
 * visible. A field that is hidden resolves to its empty value (`[]` or `null`) so the card
 * can render without re-checking visibility.
 */
export interface VisibleCardFields {
  labels: readonly BoardPayloadLabel[]
  assignees: readonly BoardPayloadAssignee[]
  author: BoardPayloadAuthor | null
  milestone: BoardPayloadMilestone | null
  repository: BoardPayloadRepository | null
  /** Always `null` on a pull request, which has no issue type. */
  issueType: BoardPayloadIssueType | null
  /** Always empty on a pull request, which has no custom fields. */
  issueFields: CardIssueFields
  /** `null` on a pull request, or an issue with no parent. */
  parentIssue: BoardPayloadParentIssue | null
  /** `null` on a pull request, or an issue with no sub-issues. */
  subIssuesProgress: BoardPayloadSubIssuesProgress | null
  /** ISO 8601 timestamps. `closed` is `null` until the item is closed or merged. */
  created: string | null
  updated: string | null
  closed: string | null
  /**
   * Always empty on a pull request. Deferred, so empty too until the host's values arrive: the
   * caller merges them into the item first.
   */
  linkedPullRequests: readonly BoardPayloadLinkedPullRequest[]
  /** Every linked pull request, including those beyond the ten in `linkedPullRequests`. */
  linkedPullRequestCount: number
}

/**
 * Resolves the attributable field values a card should render from a payload item, keeping
 * only the fields named in `visibleFieldIdentifiers`. Custom fields are found through
 * `fieldDefinitions`, since only the definition says what type of value a `field.<id>` holds.
 */
export function resolveVisibleCardFields(
  item: BoardPayloadItem,
  visibleFieldIdentifiers: ReadonlySet<string>,
  fieldDefinitions: readonly FieldDefinition[] = [],
): VisibleCardFields {
  const isVisible = (identifier: string) => visibleFieldIdentifiers.has(identifier)
  const linkedPullRequests =
    item.type === 'issue' && isVisible(FieldIdentifier.LINKED_PULL_REQUESTS)
      ? item.fields['linked-pull-requests']
      : undefined

  return {
    labels: isVisible(FieldIdentifier.LABEL) ? (item.fields.label?.value ?? []) : [],
    assignees: isVisible(FieldIdentifier.ASSIGNEE) ? (item.fields.assignee?.value ?? []) : [],
    author: isVisible(FieldIdentifier.AUTHOR) ? (item.fields.author?.value ?? null) : null,
    milestone: isVisible(FieldIdentifier.MILESTONE) ? (item.fields.milestone?.value ?? null) : null,
    repository: isVisible(FieldIdentifier.REPOSITORY) ? (item.fields.repository?.value ?? null) : null,
    issueType:
      item.type === 'issue' && isVisible(FieldIdentifier.ISSUE_TYPE) ? (item.fields.issue_type?.value ?? null) : null,
    issueFields:
      item.type === 'issue'
        ? resolveIssueFields(item, fieldDefinitions, isVisible)
        : {singleSelect: [], multiSelect: []},
    parentIssue:
      item.type === 'issue' && isVisible(FieldIdentifier.PARENT_ISSUE)
        ? (item.fields['parent-issue']?.value ?? null)
        : null,
    subIssuesProgress: item.type === 'issue' ? resolveSubIssuesProgress(item, isVisible) : null,
    created: isVisible(FieldIdentifier.CREATED) ? (item.fields.created?.value ?? null) : null,
    updated: isVisible(FieldIdentifier.UPDATED) ? (item.fields.updated?.value ?? null) : null,
    closed: isVisible(FieldIdentifier.CLOSED) ? (item.fields.closed?.value ?? null) : null,
    linkedPullRequests: linkedPullRequests?.value ?? [],
    linkedPullRequestCount: linkedPullRequests?.totalCount ?? 0,
  }
}

function resolveSubIssuesProgress(
  item: BoardPayloadIssueItem,
  isVisible: (identifier: string) => boolean,
): BoardPayloadSubIssuesProgress | null {
  if (!isVisible(FieldIdentifier.SUB_ISSUES_PROGRESS)) return null
  const progress = item.fields['sub-issues-progress']?.value ?? null
  return progress && progress.total > 0 ? progress : null
}

function resolveIssueFields(
  item: BoardPayloadIssueItem,
  fieldDefinitions: readonly FieldDefinition[],
  isVisible: (identifier: string) => boolean,
): CardIssueFields {
  const singleSelect: CardSingleSelect[] = []
  const multiSelect: CardMultiSelect[] = []
  for (const {identifier, label: fieldName, dataType} of fieldDefinitions) {
    if (!isCustomFieldIdentifier(identifier) || !isVisible(identifier)) continue
    const value = item.fields[identifier]?.value
    if (dataType === FieldDataType.SINGLE_SELECT && isBoardPayloadSelectOption(value)) {
      singleSelect.push({identifier, fieldName, option: value})
    } else if (dataType === FieldDataType.MULTI_SELECT && Array.isArray(value)) {
      const options = value.filter(isBoardPayloadSelectOption)
      if (options.length > 0) multiSelect.push({identifier, fieldName, options})
    }
  }
  return {singleSelect, multiSelect}
}

function isCustomFieldIdentifier(identifier: string): identifier is CustomFieldIdentifier {
  return /^field\.\d+$/.test(identifier)
}
